import { Box, Button, Text } from '@nimbus-ds/components';
import { EmptyMessage } from '@nimbus-ds/patterns';
import { ExclamationTriangleIcon } from '@nimbus-ds/icons';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The three page states homologation's design checklist requires: **initial/empty**, **error** and
 * **loading**. Built once here and shared by every page.
 *
 * The **loading** third lives next door in `skeletons.tsx`. It used to be one `LoadingState` — `n`
 * full-width 72px bars — used by all eight loading surfaces, which reserved the right shape for
 * none of them and made every page jump as its data landed. There is no generic loader here on
 * purpose: a skeleton that does not match its content is not a cheaper skeleton, it is a wrong
 * one, so each surface imports the shape it is actually about to render.
 *
 * 🚨 **Nimbus has no `ErrorState`.** Verified against the installed packages (`patterns@1.35.1`,
 * `components@5.59.0`): `EmptyMessage` and `Skeleton` exist, `ErrorState` does not. So the error
 * template is composed from `EmptyMessage` + a danger icon + a retry action rather than imported —
 * and it lives here so eight pages cannot improvise eight different ones, which is exactly what the
 * checklist is looking for.
 *
 * Copy comes from the shared `states.*` i18n keys in all three locales, so a page only supplies what
 * is specific to it.
 */

/**
 * Something failed to load. Always offers a way out.
 *
 * An error with no retry is a dead end — the merchant's only recourse is reloading the iframe, which
 * inside the Nuvemshop admin is not obvious. `onRetry` is required for that reason.
 */
export function ErrorState({
  onRetry,
  title,
  text,
}: {
  onRetry: () => void;
  title?: string;
  text?: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <EmptyMessage
      title={title ?? t('states.errorTitle')}
      text={text ?? t('states.errorText')}
      icon={<ExclamationTriangleIcon size="medium" />}
      actions={
        <Button appearance="primary" onClick={onRetry}>
          {t('states.retry')}
        </Button>
      }
    />
  );
}

/**
 * Nothing here yet. `action` is optional because not every empty state has a next step — a metrics
 * page with no data yet is waiting on buyers, not on the merchant.
 */
export function EmptyState({
  title,
  text,
  action,
  icon,
}: {
  title: string;
  text?: string;
  action?: ReactNode;
  icon?: ReactNode;
}): JSX.Element {
  return <EmptyMessage title={title} {...(text ? { text } : {})} icon={icon} actions={action} />;
}

/**
 * The inline "nothing in this list" row, for lists that sit inside a card and would look wrong with
 * a full `EmptyMessage`. Keeps the wording centralised even where the layout differs.
 */
export function EmptyRow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box display="flex" justifyContent="center" padding="4">
      <Text color="neutral-textLow">{children}</Text>
    </Box>
  );
}
